import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Carrot, Truck, FileScan, SprayCan, BookOpen, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import { PasswordInput } from "@/components/ui-kit";
import { changePasswordApi } from "@/lib/api";

const ADMIN_LINKS = [
  { to: "/plans", icon: BookOpen, title: "Plans APPCC", desc: "Documents del sistema (editables)" },
  { to: "/ingredients", icon: Carrot, title: "Ingredients", desc: "Catàleg + FT + al·lèrgens" },
  { to: "/providers", icon: Truck, title: "Proveïdors", desc: "Registre de proveïdors (Pla 5)" },
  { to: "/albaran-templates", icon: FileScan, title: "Models d'albarà", desc: "Extracció IA per proveïdor" },
  { to: "/cleaning-products", icon: SprayCan, title: "Productes de neteja", desc: "FT de neteja (Pla 2)" },
];

const USERS = ["MARC", "LUCIA", "HUGO", "PRIYA"];

export default function Admin() {
  const [target, setTarget]   = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!target)            return toast.error("Tria un usuari");
    if (newPass.length < 8) return toast.error("Minium 8 caracters");
    if (newPass !== confirm) return toast.error("Les contrasenyes no coincideixen");
    setLoading(true);
    try {
      await changePasswordApi(target, newPass);
      toast.success("Contrasenya de " + target.charAt(0) + target.slice(1).toLowerCase() + " actualitzada");
      setTarget(""); setNewPass(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error canviant la contrasenya");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header title="Administracio" />
      <div className="mx-auto max-w-2xl px-4 pb-12 pt-6 sm:px-6">
        <div className="mb-6 animate-fade-up flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ink text-white">
            <KeyRound size={22} />
          </div>
          <div>
            <div className="label-eyebrow">Admin</div>
            <h2 className="font-display text-2xl font-bold text-ink">Administració</h2>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-3 animate-fade-up sm:grid-cols-2">
          {ADMIN_LINKS.map(({ to, icon: Icon, title, desc }) => (
            <Link key={to} to={to} className="group flex items-center gap-3 rounded-2xl border border-line bg-white p-4 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-soft">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted text-ink"><Icon size={20} /></span>
              <span className="flex-1">
                <span className="block font-display text-base font-bold text-ink">{title}</span>
                <span className="block text-xs text-ink-soft">{desc}</span>
              </span>
              <ChevronRight size={18} className="text-ink-soft transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
        </div>

        <div className="mb-3 label-eyebrow">Contrasenyes</div>
        <form onSubmit={handleSubmit} className="space-y-5 animate-fade-up" style={{ animationDelay: "60ms" }}>
          <div className="rounded-2xl border border-line bg-white p-5 shadow-card">
            <div className="label-eyebrow mb-3">Usuari</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {USERS.map((u) => (
                <button key={u} type="button" onClick={() => setTarget(u)}
                  className={
                    "h-14 rounded-xl font-display text-base font-bold tracking-wide transition-all " +
                    (target === u ? "bg-ink text-white shadow-soft" : "bg-muted text-ink-soft hover:bg-cream")
                  }>
                  {u.charAt(0) + u.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-line bg-white p-5 shadow-card space-y-4">
            <div>
              <label className="label-eyebrow mb-2 block">Nova contrasenya</label>
              <PasswordInput value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="Minim 8 caracters" />
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Confirmar contrasenya</label>
              <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeteix la contrasenya" />
            </div>
          </div>
          <button type="submit" disabled={loading || !target || !newPass || !confirm}
            className="h-14 w-full rounded-xl bg-ink font-display text-base font-bold text-white shadow-soft transition-all hover:bg-ink/90 disabled:opacity-50">
            {loading ? "Guardant..." : "Canviar contrasenya"}
          </button>
        </form>
      </div>
    </>
  );
}
